import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import * as Fonts from "../../components/Fonts";
import { PlusTag } from "../../components/PlusTag";
import { SubHeader } from "../../components/SubHeader";
import { useAppState } from "../../contexts/App";
import { PlusBenefit, usePlusBenefits } from "../../hooks/PlusBenefits";
import CheckIcon from "../../icons/Check";
import ChevronRightIcon from "../../icons/ChevronRight";
import { hasActiveSubscription } from "../../services/api";
import { Container, Hero, List, ManageLink, Section } from "./styles";

const PlusBenefitsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appState } = useAppState();
  const { tracked, included, used, total } = usePlusBenefits();

  if (!hasActiveSubscription(appState.editingSiteSubscription)) {
    return <Navigate to="/keepo" replace />;
  }

  const open = (benefit: PlusBenefit) => {
    if (benefit.external) {
      window.open(benefit.to, "_blank");
      return;
    }
    // Help (and its chat) has its own way back: tell it to come back here
    navigate(
      benefit.to,
      benefit.to.startsWith("/help")
        ? { state: { back: { label: t("pages.plusBenefits.title"), to: "/keepo/plus" } } }
        : undefined
    );
  };

  const available = tracked.filter(benefit => !benefit.inUse);
  const inUse = tracked.filter(benefit => benefit.inUse);

  return (
    <Container>
      <SubHeader title={t("pages.plusBenefits.title")} crumbs={[]} />

      <Section>
        <Hero>
          <PlusTag className="tag">✦ Keepo Plus</PlusTag>
          <div className="usage">
            <p className="count">
              <strong>{used}</strong> {t("pages.plusBenefits.hero.of", { total })}
            </p>
            <div
              className="progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={used}
              aria-label={t("pages.plusBenefits.progress", { used, total })}>
              <span style={{ width: `${(used / total) * 100}%` }} />
            </div>
          </div>
          <p className="description">
            {available.length ? t("pages.plusBenefits.hero.description") : t("pages.plusBenefits.hero.allUsed")}
          </p>
        </Hero>
      </Section>

      {!!available.length && (
        <Section>
          <Fonts.Label>{t("pages.plusBenefits.sections.available")}</Fonts.Label>
          <List>
            {available.map(benefit => (
              <li key={benefit.key}>
                <button type="button" onClick={() => open(benefit)}>
                  <span className="text">
                    <span className="title">{benefit.title}</span>
                    <span className="description">{benefit.description}</span>
                  </span>
                  <span className="use">{t("pages.plusBenefits.use")} →</span>
                </button>
              </li>
            ))}
          </List>
        </Section>
      )}

      {!!inUse.length && (
        <Section>
          <Fonts.Label>{t("pages.plusBenefits.sections.inUse")}</Fonts.Label>
          <List>
            {inUse.map(benefit => (
              <li key={benefit.key}>
                <button type="button" onClick={() => open(benefit)}>
                  <span className="check">
                    <CheckIcon width={10} height={10} color="currentColor" />
                  </span>
                  <span className="text">
                    <span className="title">{benefit.title}</span>
                    <span className="description">{benefit.description}</span>
                  </span>
                  <span className="chevron">
                    <ChevronRightIcon width={12} height={12} color="currentColor" />
                  </span>
                </button>
              </li>
            ))}
          </List>
        </Section>
      )}

      <Section>
        <Fonts.Label>{t("pages.plusBenefits.sections.included")}</Fonts.Label>
        <List>
          {included.map(benefit => (
            <li key={benefit.key}>
              <button type="button" onClick={() => open(benefit)}>
                <span className="text">
                  <span className="title">{benefit.title}</span>
                  <span className="description">{benefit.description}</span>
                </span>
                <span className="chevron">
                  <ChevronRightIcon width={12} height={12} color="currentColor" />
                </span>
              </button>
            </li>
          ))}
        </List>
      </Section>

      <ManageLink type="button" onClick={() => navigate(`/subscription/detail?site=${appState.editingSiteId}`)}>
        {t("pages.plusBenefits.manageSubscription")}
      </ManageLink>
    </Container>
  );
};

export default PlusBenefitsPage;
